import { supabase } from '../lib/supabase';
import { Is, Sofor } from '../types';
import logger from '../utils/logger';

// DB sütunları (gerçek şema):
// arac_tipi, bize_kesilen, bizim_fiyat_kdv, created_at, created_by,
// fatura_kesildi, firma, id, kdv_bize, kdv_musteri, kim_oduyor,
// komisyon, komisyon_turu, musteri_fiyat_kdv, musteri_fiyati, notlar,
// odeme_alindi, odeme_tamamlandi, odeme_turu, odendi, plaka, sofor_adi,
// sofor_dorse, sofor_ikinci_tel, sofor_plaka, sofor_tc, sofor_tel,
// tamamlandi, tamamlanma_tarihi, tarih, team_id, teslim_yeri, ucret,
// updated_at, yuk_miktari, yuklenecek_yer

function mapRow(data: any): Is {
    return {
        id: data.id,
        nereden: data.yuklenecek_yer || '',
        nereye: data.teslim_yeri || '',
        firma: data.firma,
        yuk: data.yuk_miktari || '',
        kimOduyor: data.kim_oduyor || '',
        fiyat: data.ucret || 0,
        bizimFiyatimiz: data.bize_kesilen || undefined,
        musteriFiyati: data.musteri_fiyati || undefined,
        komisyon: data.komisyon || undefined,
        odemeDurumu: data.komisyon_turu || undefined,
        ucret: data.ucret,
        aciklama: data.notlar,
        kdvEklendi: data.kdv_bize || false,
        bizimFiyatKdv: data.bizim_fiyat_kdv || false,
        musteriFiyatKdv: data.musteri_fiyat_kdv || data.kdv_musteri || false,
        tamamlandi: data.tamamlandi,
        eklenmeTarihi: data.created_at ? new Date(data.created_at) : new Date(),
        tamamlanmaTarihi: data.tamamlanma_tarihi ? new Date(data.tamamlanma_tarihi) : undefined,
        teslimTarihi: data.tarih ? new Date(data.tarih) : undefined,
        faturaKesildi: data.fatura_kesildi || false,
        komisyonAlindi: data.komisyon_alindi || false,
        odemeAlindi: data.odendi || false,
        odemeYapildi: data.odeme_tamamlandi || false,
        createdBy: data.created_by || undefined,
        odemeTarihi: undefined,
        irsaliyeResmi: undefined,
        teslimKağıdıResmi: undefined,
        faturaResmi: undefined,
        teamId: data.team_id,
        sofor: data.sofor_adi ? {
            adSoyad: data.sofor_adi,
            telefon: data.sofor_tel || '',
            plaka: data.sofor_plaka || data.plaka || '',
            tc: data.sofor_tc || undefined,
            dorse: data.sofor_dorse || undefined,
            aracTipi: data.arac_tipi || undefined,
            telefon2: data.sofor_ikinci_tel || undefined,
        } as Sofor : undefined,
    };
}

function toDbRow(job: Partial<Is>): Record<string, any> {
    const db: Record<string, any> = {};
    if (job.nereden !== undefined) db.yuklenecek_yer = job.nereden;
    if (job.nereye !== undefined) db.teslim_yeri = job.nereye;
    if (job.firma !== undefined) db.firma = job.firma;
    if (job.kimOduyor !== undefined) db.kim_oduyor = job.kimOduyor;
    if (job.fiyat !== undefined) db.ucret = job.fiyat;
    if (job.aciklama !== undefined) db.notlar = job.aciklama;
    if (job.tamamlandi !== undefined) db.tamamlandi = job.tamamlandi;
    if (job.tamamlanmaTarihi !== undefined) db.tamamlanma_tarihi = new Date(job.tamamlanmaTarihi).toISOString();
    if (job.teslimTarihi !== undefined) db.tarih = new Date(job.teslimTarihi).toISOString();
    if (job.odemeAlindi !== undefined) db.odendi = job.odemeAlindi;
    if (job.odemeYapildi !== undefined) db.odeme_tamamlandi = job.odemeYapildi;
    if (job.komisyonAlindi !== undefined) db.komisyon_alindi = job.komisyonAlindi;
    if (job.faturaKesildi !== undefined) db.fatura_kesildi = job.faturaKesildi;
    if (job.teamId !== undefined) db.team_id = job.teamId;

    // Yük miktarı
    if (job.yuk !== undefined) db.yuk_miktari = job.yuk;
    // Bize kesilen
    if (job.bizimFiyatimiz !== undefined) db.bize_kesilen = job.bizimFiyatimiz;
    // Müşteri fiyatı
    if (job.musteriFiyati !== undefined) db.musteri_fiyati = job.musteriFiyati;
    // Komisyon bilgileri
    if (job.komisyon !== undefined) db.komisyon = job.komisyon;
    if (job.odemeDurumu !== undefined) db.komisyon_turu = job.odemeDurumu;
    // KDV bilgileri
    if (job.kdvEklendi !== undefined) db.kdv_bize = job.kdvEklendi;
    if (job.bizimFiyatKdv !== undefined) db.bizim_fiyat_kdv = job.bizimFiyatKdv;
    if (job.musteriFiyatKdv !== undefined) db.musteri_fiyat_kdv = job.musteriFiyatKdv;

    // Şoför bilgileri
    if (job.sofor) {
        db.sofor_adi = job.sofor.adSoyad;
        db.sofor_tel = job.sofor.telefon;
        db.sofor_plaka = job.sofor.plaka;
        db.sofor_tc = job.sofor.tc || null;
        db.sofor_dorse = job.sofor.dorse || null;
        db.arac_tipi = job.sofor.aracTipi || null;
        db.sofor_ikinci_tel = job.sofor.telefon2 || null;
    }

    return db;
}

export const isService = {

    // Bugünkü işleri getir (tek seferlik)
    getTodayJobs: async (teamId: string): Promise<Is[]> => {
        if (!teamId) return [];
        const { data, error } = await supabase
            .from('isler')
            .select('*')
            .eq('team_id', teamId)
            .eq('tamamlandi', false);
        if (error) {
            logger.error('getTodayJobs hatası:', error);
            return [];
        }
        return (data || []).map(mapRow);
    },

    // Tamamlanmış işleri getir (tek seferlik, team filtreli)
    getCompletedJobs: async (teamId: string): Promise<Is[]> => {
        if (!teamId) return [];
        const { data, error } = await supabase
            .from('isler')
            .select('*')
            .eq('team_id', teamId)
            .eq('tamamlandi', true);
        if (error) {
            logger.error('getCompletedJobs hatası:', error);
            return [];
        }
        return (data || []).map(mapRow);
    },

    // Tamamlanmış işleri getir (tek seferlik - eski)
    getTamamlanmisIsler: async (teamId?: string): Promise<Is[]> => {
        let query = supabase
            .from('isler')
            .select('*')
            .eq('tamamlandi', true);
        if (teamId) query = query.eq('team_id', teamId);
        const { data, error } = await query;
        if (error) throw error;
        return (data || []).map(mapRow);
    },

    // Yeni iş ekle
    addJob: async (job: Omit<Is, 'id' | 'eklenmeTarihi'>, teamId?: string) => {
        if (!teamId) throw new Error('Team ID gerekli (İş ekleme)');

        const dbRow = toDbRow(job as Partial<Is>);

        const { data, error } = await supabase
            .from('isler')
            .insert({ ...dbRow, team_id: teamId })
            .select()
            .single();

        if (error) throw error;
        return mapRow(data);
    },

    // İş güncelle
    updateJob: async (id: string, updates: Partial<Is>, teamId?: string) => {
        const dbRow = toDbRow(updates);

        logger.debug('Supabase güncelleme verileri:', dbRow);
        logger.debug('Güncellenecek kayıt ID:', id);

        let query = supabase.from('isler').update(dbRow).eq('id', id);
        if (teamId) query = query.eq('team_id', teamId);
        const { error } = await query;
        if (error) {
            logger.error('Supabase güncelleme hatası:', error);
            throw error;
        }
        logger.debug('İş başarıyla güncellendi');
    },

    // İş sil (ilişkili kayıtları da temizle)
    deleteJob: async (id: string, teamId?: string) => {
        // Önce ilişkili tabloları temizle (foreign key constraint önleme)
        await supabase.from('komisyonlar').delete().eq('is_id', id);
        await supabase.from('odemeler').delete().eq('is_id', id);
        await supabase.from('faturalar').delete().eq('is_id', id);
        await supabase.from('alacaklar').delete().eq('is_id', id);

        let query = supabase.from('isler').delete().eq('id', id);
        if (teamId) query = query.eq('team_id', teamId);
        const { error } = await query;
        if (error) {
            logger.error('İş silme hatası:', error.message, error.details, error.hint);
            throw error;
        }
    },

    // İşi tamamla
    completeJob: async (id: string, sofor: Sofor, jobData?: { komisyon?: number; odemeDurumu?: string }, teamId?: string) => {
        logger.debug('Şoför verileri:', sofor);
        logger.debug('İş verileri:', jobData);

        let jobQuery = supabase.from('isler').select('*').eq('id', id);
        if (teamId) jobQuery = jobQuery.eq('team_id', teamId);
        const { data: job, error: jobError } = await jobQuery.single();
        if (jobError || !job) throw new Error('İş bulunamadı');

        const updateData: Record<string, any> = {
            tamamlandi: true,
            tamamlanma_tarihi: new Date().toISOString(),
            sofor_adi: sofor.adSoyad,
            sofor_tel: sofor.telefon,
            sofor_plaka: sofor.plaka || null,
            sofor_tc: sofor.tc || null,
            sofor_dorse: sofor.dorse || null,
            arac_tipi: sofor.aracTipi || null,
            sofor_ikinci_tel: sofor.telefon2 || null,
        };

        if (jobData?.odemeDurumu) updateData.komisyon_turu = jobData.odemeDurumu;
        if (jobData?.komisyon !== undefined) updateData.komisyon = jobData.komisyon;

        const { error: updateError } = await supabase.from('isler').update(updateData).eq('id', id);
        if (updateError) throw updateError;

        try {
            let orQuery = [];
            if (sofor.telefon) orQuery.push(`telefon.eq.${sofor.telefon}`);
            if (sofor.plaka) orQuery.push(`plaka.eq.${sofor.plaka}`);

            let existingSofor = null;
            if (orQuery.length > 0) {
                const { data } = await supabase
                    .from('soforler')
                    .select('*')
                    .eq('team_id', job.team_id)
                    .or(orQuery.join(','))
                    .limit(1)
                    .single();
                existingSofor = data;
            }

            if (existingSofor) {
                await supabase.from('soforler').update({
                    ad_soyad: sofor.adSoyad,
                    telefon: sofor.telefon || existingSofor.telefon,
                    plaka: sofor.plaka || existingSofor.plaka,
                    tc: sofor.tc || existingSofor.tc,
                    arac_tipi: sofor.aracTipi || existingSofor.arac_tipi,
                    dorse: sofor.dorse || existingSofor.dorse,
                    ikinci_telefon: sofor.telefon2 || existingSofor.ikinci_telefon || null,
                    toplam_is_sayisi: (existingSofor.toplam_is_sayisi || 0) + 1
                }).eq('id', existingSofor.id);
            } else {
                await supabase.from('soforler').insert({
                    ad_soyad: sofor.adSoyad,
                    telefon: sofor.telefon,
                    plaka: sofor.plaka,
                    tc: sofor.tc,
                    arac_tipi: sofor.aracTipi,
                    dorse: sofor.dorse,
                    ikinci_telefon: sofor.telefon2 || null,
                    toplam_is_sayisi: 1,
                    team_id: job.team_id
                });
            }
        } catch (e) {
            logger.error('Şoför kaydetme hatası:', e);
        }

        try {
            const komisyonTutari = jobData?.komisyon;
            const odemeTuruSon = jobData?.odemeDurumu || job.komisyon_turu;
            if (komisyonTutari !== undefined && komisyonTutari !== null && odemeTuruSon) {
                await supabase.from('komisyonlar').insert({
                    is_id: id,
                    team_id: job.team_id,
                    tutar: komisyonTutari,
                    tur: odemeTuruSon,
                    alindi: odemeTuruSon !== 'fatura'
                });
            }
        } catch (e) {
            logger.error('Komisyon kopyalama hatası:', e);
        }

        try {
            if (job.kim_oduyor === 'biz') {
                await supabase.from('odemeler').insert({
                    is_id: id,
                    team_id: job.team_id,
                    tutar: job.bize_kesilen || job.ucret || 0,
                    odendi: false
                });
            }

            if (job.fatura_kesildi === false || job.fatura_kesildi === null) {
                await supabase.from('faturalar').insert({
                    is_id: id,
                    team_id: job.team_id,
                    musteri_fiyati: job.musteri_fiyati || 0,
                    bize_kesilen: job.bize_kesilen || 0,
                    kesildi: false
                });
            } else if (job.fatura_kesildi === true) {
                await supabase.from('alacaklar').insert({
                    is_id: id,
                    team_id: job.team_id,
                    tutar: job.musteri_fiyati || 0,
                    alindi: false
                });
            }
        } catch (e) {
            logger.error('Fatura/Alacak/Ödeme kopyalama hatası:', e);
        }
    },

    // Fatura kesildi olarak işaretle
    markInvoiceIssued: async (id: string, teamId?: string) => {
        let query = supabase.from('isler').update({ fatura_kesildi: true }).eq('id', id);
        if (teamId) query = query.eq('team_id', teamId);
        const { error } = await query;
        if (error) throw error;
    },

    // Ödeme alındı olarak işaretle
    markPaymentReceived: async (id: string, teamId?: string) => {
        let query = supabase.from('isler').update({ odendi: true }).eq('id', id);
        if (teamId) query = query.eq('team_id', teamId);
        const { error } = await query;
        if (error) throw error;
    }
};
